// Alert sound using Web Audio API — no external files needed
let audioContext = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

export function playAlertSound() {
  try {
    const ctx = getAudioContext();

    // First tone — urgent high beep
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "square";
    osc1.frequency.value = 880;
    gain1.gain.setValueAtTime(0.3, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.15);

    // Second tone — slightly lower, creates urgency pattern
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "square";
    osc2.frequency.value = 660;
    gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.2);
    osc2.stop(ctx.currentTime + 0.35);

    // Third tone — repeat high for alarm pattern
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = "square";
    osc3.frequency.value = 880;
    gain3.gain.setValueAtTime(0.3, ctx.currentTime + 0.4);
    gain3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.55);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(ctx.currentTime + 0.4);
    osc3.stop(ctx.currentTime + 0.55);
  } catch (e) {
    // Audio not supported or blocked by browser
    console.debug("alertSound (default)", e);
  }
}

/**
 * Police siren — sweeping two-tone wail.
 * Used for banditry alerts to convey heightened urgency.
 */
export function playPoliceSiren() {
  try {
    const ctx = getAudioContext();
    const SWEEPS = 4;          // number of wail cycles
    const SWEEP_DUR = 0.55;    // seconds per up-down sweep
    const LOW = 600;           // Hz
    const HIGH = 1200;         // Hz

    for (let i = 0; i < SWEEPS; i++) {
      const t0 = ctx.currentTime + i * SWEEP_DUR;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";   // rougher, police-like timbre
      osc.frequency.setValueAtTime(LOW, t0);
      osc.frequency.linearRampToValueAtTime(HIGH, t0 + SWEEP_DUR / 2);
      osc.frequency.linearRampToValueAtTime(LOW, t0 + SWEEP_DUR);
      gain.gain.setValueAtTime(0.0, t0);
      gain.gain.linearRampToValueAtTime(0.35, t0 + 0.02);
      gain.gain.setValueAtTime(0.35, t0 + SWEEP_DUR - 0.03);
      gain.gain.linearRampToValueAtTime(0.0, t0 + SWEEP_DUR);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + SWEEP_DUR);
    }
  } catch (e) {
    // Audio not supported or blocked by browser
    console.debug("alertSound (siren)", e);
  }
}

/**
 * Short attention chime — two quick rising beeps. Used to preface TTS
 * so the driver's ear is caught before the spoken caution is read aloud.
 */
export function playAttentionChime() {
  try {
    const ctx = getAudioContext();
    const tones = [
      { freq: 784, start: 0, dur: 0.12 },  // G5
      { freq: 1047, start: 0.14, dur: 0.18 }, // C6
    ];
    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = t.freq;
      const t0 = ctx.currentTime + t.start;
      gain.gain.setValueAtTime(0.0, t0);
      gain.gain.linearRampToValueAtTime(0.35, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, t0 + t.dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + t.dur);
    }
  } catch (e) {
    // Audio not supported or blocked
    console.debug("alertSound (chime)", e);
  }
}

/**
 * Multilingual prefix + alert-type labels used to localize the spoken
 * header of every alert announcement. Pidgin uses the English voice but
 * reads in Pidgin phrasing — the Web Speech API has no Pidgin voice, but
 * casual Nigerian English reads naturally enough.
 */
const LANG_SHORT = {
  "en-US": "en",
  "en-GB": "en",
  "yo-NG": "yo",
  "ha-NG": "ha",
  "ig-NG": "ig",
  pcm: "pcm",
};

// Short, spoken-form alert-type names in each supported language. When the
// backend translate endpoint is unavailable (network/LLM failure), these
// give us at minimum a localized header and type — better than the English
// fallback in a driving-safety scenario.
const TYPE_LABELS = {
  en: { security: "Security alert", fire: "Fire emergency", medical: "Medical emergency", accident: "Accident", banditry: "Banditry alert", ecaution: "Caution", location_share: "Location", safe_check_in: "Safe check-in" },
  yo: { security: "Ìkìlọ̀ ààbò", fire: "Pajawiri ináà", medical: "Pajawiri ìlera", accident: "Ijàmbá", banditry: "Ìkìlọ̀ àwọn janduku", ecaution: "Ṣọra", location_share: "Ibùdókọ̀", safe_check_in: "Àìní ewu" },
  ha: { security: "Gargadin tsaro", fire: "Gaggawa wuta", medical: "Gaggawa lafiya", accident: "Hatsari", banditry: "Gargadin 'yan bindiga", ecaution: "A yi hankali", location_share: "Wuri", safe_check_in: "Lafiya lau" },
  ig: { security: "Ịdọ aka ná ntị nche", fire: "Ihe mberede ọkụ", medical: "Ihe mberede ahụike", accident: "Ihe mberede", banditry: "Ịdọ aka ná ntị ndị na-apụnara mmadụ ihe", ecaution: "Lezie anya", location_share: "Ebe", safe_check_in: "Nọọ n'udo" },
  pcm: { security: "Security wahala", fire: "Fire don start", medical: "Medical emergency", accident: "Accident don happen", banditry: "Bandit wahala", ecaution: "Take am easy o", location_share: "Location", safe_check_in: "I dey safe" },
};

/**
 * Format a distance in metres into a spoken phrase in the target language.
 * Returns an empty string for unknown distances so the caller can omit it.
 */
function distancePhrase(distanceM, langShort) {
  if (typeof distanceM !== "number" || isNaN(distanceM) || distanceM <= 0) return "";
  const rounded = distanceM >= 1000
    ? `${(distanceM / 1000).toFixed(1)} kilometres`
    : `${Math.round(distanceM)} metres`;
  switch (langShort) {
    case "yo":  return `${rounded} sí ọ̀dọ̀ rẹ.`;
    case "ha":  return `${rounded} daga gare ku.`;
    case "ig":  return `${rounded} site n'ebe ị nọ.`;
    case "pcm": return `${rounded} from where you dey.`;
    default:    return `${rounded} away.`;
  }
}

/**
 * Read a citizen's preferred TTS language from localStorage.
 * Defaults to en-US. Stored under `guard_tts_lang`.
 */
export function getPreferredTtsLang() {
  try {
    return localStorage.getItem("guard_tts_lang") || "en-US";
  } catch {
    return "en-US";
  }
}

export function setPreferredTtsLang(lang) {
  try { localStorage.setItem("guard_tts_lang", lang); } catch (e) { console.debug("setPreferredTtsLang", e); }
}

/**
 * Speak the given text aloud using the Web Speech API.
 * Used for eCaution alerts so drivers can hear warnings without looking at the screen.
 * Silently no-ops if SpeechSynthesis is unavailable or text is empty.
 *
 * If the requested `lang` voice isn't available on the device, falls back to English
 * gracefully — the text still reads, just in the default voice.
 */
export function playTTS(text, opts = {}) {
  try {
    if (!text || typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(String(text));
    utter.rate = opts.rate ?? 1.0;
    utter.pitch = opts.pitch ?? 1.0;
    utter.volume = opts.volume ?? 1.0;
    const requestedLang = opts.lang ?? getPreferredTtsLang();
    utter.lang = requestedLang;
    const voices = synth.getVoices?.() || [];
    // Prefer an exact-match voice for the requested locale; fall back to any en-* voice
    const exact = voices.find((v) => v.lang === requestedLang);
    const langPrefix = requestedLang.split("-")[0];
    const sameLang = voices.find((v) => v.lang?.toLowerCase().startsWith(langPrefix));
    const englishFallback = voices.find((v) => /^en/i.test(v.lang));
    const picked = exact || sameLang || englishFallback;
    if (picked) utter.voice = picked;
    synth.speak(utter);
  } catch (e) {
    // Speech synthesis not supported or blocked
    console.debug("alertSound (TTS)", e);
  }
}

/**
 * Call the backend translate endpoint. Returns the translated text on
 * success, or the original text on any failure (so the speaker still says
 * SOMETHING).  Small client-side memoisation on top of the server-side
 * cache — same (text, lang) pair during a session won't even round-trip.
 */
const _translateCache = new Map();
async function translateForSpeech(text, langShort) {
  if (!text) return "";
  if (!langShort || langShort === "en") return text;
  const cacheKey = `${langShort}|${text}`;
  if (_translateCache.has(cacheKey)) return _translateCache.get(cacheKey);
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${backend}/api/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, target_lang: langShort }),
      // Translation is "best effort" — if the LLM is slow, we'd rather
      // speak English than wait more than 6s for a driver alert.
      signal: AbortSignal.timeout?.(6000),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json();
    const out = data?.text || text;
    _translateCache.set(cacheKey, out);
    return out;
  } catch {
    return text;
  }
}

/**
 * Announce an alert aloud in the user's chosen language — covers ALL alert
 * types (not just eCaution). The announcement follows the same pattern in
 * every language:
 *
 *   <chime>  <type-header>.  <localized description>.  <distance phrase>.
 *
 * - `alertType` picks the localized header (Fire emergency / Pajawiri ináà / ...)
 * - `description` is translated via the backend `/api/translate` endpoint
 *   (Claude Sonnet 4.5, cached in mongo). Falls back to English if
 *   translation fails or times out (> 6 s).
 * - `distanceM` is converted into a localized "450 metres away" phrase
 *   when provided.
 *
 * Safe to call with partial arguments — missing pieces are silently dropped.
 */
export async function playSpokenAlert(alertType, description, distanceM) {
  playAttentionChime();
  const lang = getPreferredTtsLang();
  const langShort = LANG_SHORT[lang] || "en";
  const typeLabel = (TYPE_LABELS[langShort] || TYPE_LABELS.en)[alertType] || "";
  const [translatedDesc, dist] = await Promise.all([
    description ? translateForSpeech(String(description), langShort) : Promise.resolve(""),
    Promise.resolve(distancePhrase(distanceM, langShort)),
  ]);
  const parts = [typeLabel, translatedDesc, dist].filter(Boolean);
  const message = parts.join(". ").replace(/\.\.+/g, ".").replace(/\s+/g, " ").trim();
  if (!message) return;
  // 350 ms so the chime finishes before the TTS voice starts
  setTimeout(() => playTTS(message, { lang }), 350);
}

/**
 * Legacy wrapper kept for callers that specifically want the eCaution
 * flavour (headline "Caution") without providing a distance.
 */
export function playECaution(description, distanceM) {
  return playSpokenAlert("ecaution", description, distanceM);
}

/**
 * Speak a free-form driving advisory (e.g. AI-recommended route reasoning).
 * No chime, no type-prefix — just translate the sentence into the user's
 * preferred language and speak it cleanly so the driver's eyes stay on the
 * road.
 */
export async function speakAdvisory(text) {
  if (!text) return;
  const lang = getPreferredTtsLang();
  const langShort = LANG_SHORT[lang] || "en";
  const translated = await translateForSpeech(String(text), langShort);
  playTTS(translated || String(text), { lang });
}

/**
 * Convenience: pick the right sound for a given alert type.
 * - Banditry → police siren, then a localized TTS announcement
 * - eCaution → attention chime + localized TTS of the description
 * - Location share → silent (presence-only)
 * - Everything else → default 3-tone beep, then a localized TTS announcement
 *
 * The localized voice-over is what lets Yoruba / Hausa / Igbo / Pidgin
 * users understand the full alert WITHOUT needing to read English on the
 * screen — a key safety feature for users driving or working with their
 * hands full.
 */
export function playSoundForAlert(alertType, description, distanceM) {
  if (alertType === "location_share") return;

  // 1) The attention-grabbing non-speech sound first
  if (alertType === "banditry") {
    playPoliceSiren();
  } else if (alertType === "ecaution") {
    // eCaution's voice handles its own chime inside playSpokenAlert
  } else {
    playAlertSound();
  }

  // 2) Spoken announcement (async, best-effort). A 400 ms delay so the
  // non-speech sound finishes cleanly before the voice starts — except
  // for eCaution which already chimes internally.
  const speak = () => { playSpokenAlert(alertType, description, distanceM); };
  if (alertType === "ecaution") {
    speak();
  } else {
    setTimeout(speak, 400);
  }
}
